import { createTheme } from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface TypeBackground {
    main: string;
    light: string;
    dark: string;
    gradient: string;
  }
  interface TypographyVariants {
    title: React.CSSProperties;
  }
  interface TypographyVariantsOptions {
    title?: React.CSSProperties;
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    menuPrimary: true;
    menuSecondary: true;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    title: true;
  }
}

const primaryMain = '#002342'; //Familiprix blue
const primaryMainOver = '#335675'; //Familiprix blue
const secondaryMain = '#FFF'; //Pure white
const controlMain = '#306CE1';
const error = '#EA0404';

const btnMenuPrimaryColor = '#99ABBA';
const btnMenuPrimaryBorderColor = '#99ABBA';

const btnMenuSecondaryColor = '#FFF';
const btnMenuSecondaryBorderColor = '#668098';

const backgroundLight = '#FFF';
const background = '#F9FCFF';
const backgroundDark = '#F4F7FA';

const textSecondary50 = 'rgb(255, 255, 255, 0.5)'; //Pure white 50% saturation

export const shadows = {
  lightBox: '0px 10px 20px rgba(0, 0, 0, 0.04), 0px 2px 6px rgba(0, 0, 0, 0.04), 0px 0px 1px rgba(0, 0, 0, 0.04)',
  familiprixHeader: '0px 4px 20px rgba(0, 40, 85, 0.05)',
};

export const lightTheme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 1041,
      lg: 1200,
      xl: 1536,
    },
  },
  components: {
    MuiStepConnector: {
      styleOverrides: {
        vertical: {
          paddingLeft: '3.5px',
        },
        horizontal: {
          paddingTop: '3.5px',
        },
      },
    },
    MuiStepIcon: {
      styleOverrides: {
        root: {
          width: '32px',
          height: '32px',
          border: '1px solid ' + secondaryMain,
          borderRadius: '50%',
          fill: 'none',
          '&>.MuiStepIcon-text': {
            letterSpacing: '0.5px', //Trick : Center text after border side effect
          },
          '&.Mui-active': {
            fill: secondaryMain,
            border: 'none',
            borderRadius: 'none',
            '&>.MuiStepIcon-text': {
              fill: primaryMain,
              letterSpacing: '0',
            },
          },
          '&.Mui-completed': {
            fill: secondaryMain,
            border: 'none',
            borderRadius: 'none',
            '&>.MuiStepIcon-text': {
              letterSpacing: '0',
            },
          },
        },
      },
    },
    MuiStepLabel: {
      styleOverrides: {
        label: {
          color: textSecondary50,
          '&.Mui-active': {
            color: secondaryMain,
            fontWeight: 'bold',
          },
          '&.Mui-completed': {
            color: textSecondary50,
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontSize: '16px',
          lineHeight: '28px',
          textTransform: 'none', // Ne pas forcer les majuscules
        },
        contained: {
          '&:hover': {
            backgroundColor: primaryMainOver,
          },
        },
      },
      variants: [
        {
          props: { variant: 'menuPrimary' },
          style: {
            color: btnMenuPrimaryColor,
            border: '1px solid ' + btnMenuPrimaryBorderColor,
            padding: '2px 12px',
          },
        },
        {
          props: { variant: 'menuSecondary' },
          style: {
            color: btnMenuSecondaryColor,
            border: '1px solid ' + btnMenuSecondaryBorderColor,
            padding: '2px 12px',
          },
        },
      ],
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          margin: '24px 0',
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        colorPrimary: {
          color: controlMain,
          '&>span>svg': {
            color: controlMain,
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        colorPrimary: {
          color: controlMain,
          '&>svg': {
            color: controlMain,
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: '#38444D',
          display: 'inline-block',
          marginBottom: '6px',
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          margin: '5px 0',
          color: error,
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          backgroundColor: '#FFF',
          '&.Mui-error': {
            color: error,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          '&.MuiPaper-elevation:not(.MuiPaper-elevation0)': {
            boxShadow: shadows.lightBox,
          },
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          '&:last-child': {
            paddingBottom: '16px',
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid #BEC6CD',
          '& .MuiTabs-indicator': {
            height: '4px',
            backgroundColor: '#2757B4',
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          padding: '12px 0',
          fontSize: '1rem',
          fontWeight: 500,
          marginRight: '24px',
          color: '#668098',
          '&:last-of-type': {
            marginRight: 0,
          },
          '&.Mui-selected': {
            color: '#002C53',
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          '&.MuiAlert-standardError': {
            color: error,
            border: '1px solid #F26868',
          },
          '&.MuiAlert-standardSuccess': {
            color: '#1c4d1e',
            border: '1px solid #68b26c',
          },
          '&.MuiAlert-standardInfo': {
            color: '#306CE1',
            border: '1px solid #ACC4F3',
            backgroundColor: '#E4ECFC',
            '& .MuiAlert-icon': {
              color: '#306CE1',
            },
          },
          '&.MuiAlert-standardWarning': {
            color: '#ed6c02',
            border: '1px solid #ff9800',
            backgroundColor: '#fff4e5',
            '& .MuiAlert-icon': {
              color: '#ed6c02',
            },
          },
        },
      },
    },
  },
  palette: {
    primary: {
      main: primaryMain, //override la couleur primary.main pour tout les composantes
    },
    error: {
      main: error,
    },
    background: {
      main: background, //override la couleur primary.main pour tout les composantes
      light: backgroundLight, //override la couleur primary.main pour tout les composantes
      dark: backgroundDark, //override la couleur primary.main pour tout les composantes
      gradient: 'linear-gradient(180deg, #154779 0%, #002C50 100%)',
    },
  },
  typography: {
    title: {
      color: primaryMain,
      fontSize: '2rem',
      lineHeight: '1.25',
      fontWeight: '700',
      paddingBottom: '24px',
    },
    h1: {
      fontSize: '2rem',
      fontWeight: 700,
      color: primaryMain,
    },
    h2: {
      fontSize: '1.5rem',
      fontWeight: 700,
      lineHeight: 1.4,
      color: primaryMain,
    },
    h3: {
      fontSize: '1.25rem',
      fontWeight: 700,
      lineHeight: 1.5,
      color: primaryMain,
    },
    body2: {
      fontSize: '0.875rem',
    },
    fontFamily: [
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(','),
  },
});
